import type { ShapeJSON } from '@takeshape/schema';
import { getSavingErrorMessage } from 'app/common/utils/handle-error';
import { FORM_NAME as CONTENT_FORM } from 'app/content/constants';
import { showMessageSaga } from 'app/messages/saga';
import type { StatusUpdate } from 'app/schedule/types';
import { getShapeById } from 'app/schema';
import { getValues } from 'app/schema-form/selectors';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getRoutineSaga } from '../common/utils/new-sagas.js';
import { onFormRegister } from '../schema-form/util/on-register.js';
import {
  type CancelStatusUpdatePayload,
  type CreateStatusUpdatePayload,
  cancelStatusUpdateRoutine,
  clearStatusUpdateList,
  type GetStatusUpdateListPayload,
  getStatusUpdateListRoutine,
  type SetScheduleType,
  scheduleStatusUpdateRoutine,
  setSchedule
} from './actions';
import { cancelStatusUpdate, createStatusUpdate, getStatusUpdateList } from './api.js';
import { getSchedule } from './selectors.js';

export function* getStatusUpdateListSaga(payload: GetStatusUpdateListPayload): SagaIterator {
  const { result } = yield call(getStatusUpdateList, {
    contentId: payload.contentId,
    contentTypeName: payload.shapeName
  });
  return result.items.reduce((data: Record<string, string>, update: StatusUpdate) => {
    return { ...data, [update.payload.status]: update.timestamp };
  }, {});
}

export function* scheduleStatusUpdateSaga(payload: CreateStatusUpdatePayload): SagaIterator {
  const input = {
    ...payload,
    contentTypeName: payload.shapeName
  };
  return yield call(createStatusUpdate, { input });
}

export function* cancelStatusUpdateSaga(payload: CancelStatusUpdatePayload): SagaIterator {
  const input = {
    ...payload,
    contentTypeName: payload.shapeName
  };
  return yield call(cancelStatusUpdate, { input });
}

export function* handleLoadStatusUpdateList(): SagaIterator {
  const contentForm = yield select(getValues, { formName: CONTENT_FORM });
  const { _id: contentId, _shapeId: shapeId } = contentForm;
  if (contentId && shapeId) {
    const shape: ShapeJSON | undefined = yield select(getShapeById, {
      shapeId
    });
    if (shape?.model) {
      return yield put(
        getStatusUpdateListRoutine.requested({
          contentId,
          shapeName: shape.name
        })
      );
    }
  }

  return yield put(clearStatusUpdateList());
}

export function* handleSaveStatusUpdates({ payload }: SetScheduleType): SagaIterator {
  const { contentId, shapeName, timestamp, status } = payload;
  const initialSchedule = yield select(getSchedule);
  const initialTimestamp = initialSchedule[status];
  if (!timestamp && initialTimestamp) {
    return yield put(
      cancelStatusUpdateRoutine.requested({
        contentId,
        shapeName,
        timestamp: initialTimestamp
      })
    );
  }

  if (timestamp && timestamp !== initialTimestamp) {
    return yield put(
      scheduleStatusUpdateRoutine.requested({
        contentId,
        shapeName,
        timestamp,
        status
      })
    );
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(setSchedule, handleSaveStatusUpdates),
    takeLatest(onFormRegister(CONTENT_FORM), handleLoadStatusUpdateList),
    takeLatest(
      getStatusUpdateListRoutine.requested,
      getRoutineSaga(getStatusUpdateListRoutine, getStatusUpdateListSaga)
    ),
    takeLatest(
      scheduleStatusUpdateRoutine.requested,
      getRoutineSaga(scheduleStatusUpdateRoutine, scheduleStatusUpdateSaga)
    ),
    takeLatest(cancelStatusUpdateRoutine.requested, getRoutineSaga(cancelStatusUpdateRoutine, cancelStatusUpdateSaga)),
    takeLatest(
      [scheduleStatusUpdateRoutine.FULFILLED, cancelStatusUpdateRoutine.FULFILLED],
      handleLoadStatusUpdateList
    ),
    takeLatest(scheduleStatusUpdateRoutine.FULFILLED, showMessageSaga('info', 'Schedule saved', 1500)),
    takeLatest(cancelStatusUpdateRoutine.FULFILLED, showMessageSaga('info', 'Schedule cancelled', 1500)),
    takeLatest(
      scheduleStatusUpdateRoutine.rejected,
      showMessageSaga('error', getSavingErrorMessage('Scheduling', 'status'))
    ),
    takeLatest(
      cancelStatusUpdateRoutine.rejected,
      showMessageSaga('error', getSavingErrorMessage('Cancelling', 'schedule'))
    )
  ]);
}
